import React, { useRef, useState } from 'react';

// Camada p/ desenhar um retângulo sobre a foto (arrastar). Devolve box normalizado 0-1000 [x0, y0, x1, y1].
// Deve ficar dentro de um container com a MESMA proporção da imagem (object-contain sem barras).
export const BoxDrawLayer = ({ active, onDone, onCancel, hint, testId = 'box-draw-layer' }) => {
  const ref = useRef(null);
  const [start, setStart] = useState(null);
  const [cur, setCur] = useState(null);
  if (!active) return null;
  const pt = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)), y: Math.max(0, Math.min(1, (e.clientY - r.top) / r.height)) };
  };
  const down = (e) => { e.preventDefault(); ref.current.setPointerCapture?.(e.pointerId); const p = pt(e); setStart(p); setCur(p); };
  const move = (e) => { if (start) setCur(pt(e)); };
  const up = () => {
    if (!start || !cur) return;
    const x0 = Math.min(start.x, cur.x), x1 = Math.max(start.x, cur.x), y0 = Math.min(start.y, cur.y), y1 = Math.max(start.y, cur.y);
    setStart(null); setCur(null);
    if (x1 - x0 < 0.03 || y1 - y0 < 0.03) return;
    onDone([Math.round(x0 * 1000), Math.round(y0 * 1000), Math.round(x1 * 1000), Math.round(y1 * 1000)]);
  };
  const box = start && cur ? { left: `${Math.min(start.x, cur.x) * 100}%`, top: `${Math.min(start.y, cur.y) * 100}%`, width: `${Math.abs(cur.x - start.x) * 100}%`, height: `${Math.abs(cur.y - start.y) * 100}%` } : null;
  return (
    <div ref={ref} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { setStart(null); setCur(null); }} className="absolute inset-0 z-20 cursor-crosshair touch-none bg-teal-900/20" data-testid={testId}>
      {box && <div className="absolute border-2 border-dashed border-white bg-teal-400/30 rounded-md shadow-lg" style={box} data-testid={`${testId}-box`} />}
      <div className="absolute inset-x-0 top-2 flex justify-center pointer-events-none"><span className="rounded-full bg-slate-900/85 text-white text-[11px] font-semibold px-3 py-1.5 shadow">{hint}</span></div>
      {onCancel && <button type="button" onClick={(e) => { e.stopPropagation(); onCancel(); }} onPointerDown={(e) => e.stopPropagation()} className="absolute bottom-2 right-2 h-8 px-3 rounded-full bg-white/90 text-slate-800 text-[11px] font-bold shadow" data-testid={`${testId}-cancel`}>✕</button>}
    </div>
  );
};

export const DOOR_TYPES = ['ENTRY_DOOR', 'PATIO_DOOR', 'NEW_DOOR'];
export const isDoorType = (type) => DOOR_TYPES.includes(type);
// Nº de folhas lado a lado (1 simples, 2 dupla, 3 tripla). Glider nunca é simples.
export const unitsOf = (w) => { const u = Math.min(5, Number(w?.units) || (w?.twin ? 2 : 1)); return w?.type === 'GL' && u < 2 ? 2 : u; };
const UNITS_STYLES = { 1: ['double_hung', 'casement', 'picture', 'gliding', 'awning', 'awning_equal', 'awning_unequal', 'awning_top_vent', 'awning_dual_vent', 'bay_bow'], 2: ['twin_gliding', 'twin_casement', 'picture', 'double_hung', 'awning', 'bay_bow'], 3: ['triple_gliding', 'triple_casement', 'picture', 'double_hung', 'awning', 'bay_bow'], 4: ['casement', 'picture', 'double_hung', 'awning'], 5: ['casement', 'picture', 'double_hung', 'awning'] };
export const stylesForUnits = (units, fromServer) => (fromServer && fromServer[String(units)]) || UNITS_STYLES[units] || UNITS_STYLES[1];
export const SHAPE_TYPES = ['EYEBROW', 'EYEBROW_LEG', 'HALF_EYEBROW', 'HALF_EYEBROW_LEG', 'HALF_ROUND', 'QUARTER_ROUND', 'QUARTER_ARCH_LEG', 'CIRCLE', 'OVAL', 'OCTAGON', 'TRAPEZOID', 'TRIANGLE', 'SPRINGLINE'];
export const isShapeType = (type) => SHAPE_TYPES.includes(type);
const SHAPE_STYLES = { default: ['picture'], TRAPEZOID: ['picture', 'casement', 'awning'] };
// Estilos oferecidos p/ uma janela: forma especial → fixa (Picture) [+casement/awning no trapézio]; senão por nº de folhas
export const stylesForWindow = (w, opts = {}) => {
  if (isShapeType(w?.type)) { const m = opts.shapeStyles || SHAPE_STYLES; return m[w.type] || m.default || SHAPE_STYLES.default; }
  if (ALWAYS_FIXED.includes(w?.type)) return ['picture'];
  return stylesForUnits(unitsOf(w), opts.unitsStyles);
};

// Regras físicas por modelo (espelho de backend/services/sales_products.py): estilo escolhido vence o tipo medido; formas especiais só abrem como casement/awning e nunca têm folha de baixo.
const FIXED_TYPES = ['PW', 'TR', 'SPW', ...SHAPE_TYPES];
const ALWAYS_FIXED = ['TR']; // transom: fixa por definição, ignora o modelo da proposta
const HUNG_TYPES = ['DH', 'SH', 'BAY', 'BOW'];
const HUNG_STYLES = ['double_hung', 'single_hung', 'bay_bow'];
const SHAPE_OPERABLE = ['casement', 'awning'];
const model = (style) => (style && style !== 'keep' ? style : null);
// Abre (pode ter tela)? Picture e formas especiais não abrem.
export const opensWindow = (type, style) => { if (isShapeType(type)) return SHAPE_OPERABLE.includes(style); if (ALWAYS_FIXED.includes(type)) return false; const m = model(style); return m ? m !== 'picture' : !FIXED_TYPES.includes(type); };
// Tem folha de cima e de baixo (vidros "— folha de baixo")? Só DH / SH / Bay / Bow.
export const hasBottomSash = (type, style) => { if (isShapeType(type) || ALWAYS_FIXED.includes(type)) return false; const m = model(style); return m ? HUNG_STYLES.includes(m) : HUNG_TYPES.includes(type); };
export const isTripleGlider = (type, style) => { const m = model(style); return m ? m === 'triple_gliding' : type === 'TSL'; };
// Casement (1–5 folhas): tela sempre inteira (interna) — sem meia tela; grade Craftsman só aqui.
export const isCasement = (type, style) => { if (isShapeType(type)) return style === 'casement'; const m = model(style); return m ? ['casement', 'twin_casement', 'triple_casement'].includes(m) : type === 'CS'; };
// Awning (simples, + picture ou dupla ventilada): tela inteira interna — sem meia tela.
export const isAwning = (type, style) => { if (isShapeType(type)) return style === 'awning'; const m = model(style); return m ? m.startsWith('awning') : type === 'AW'; };
export const screenOptionsFor = (type, style, all = []) => (!opensWindow(type, style) ? ['none'] : isTripleGlider(type, style) || isCasement(type, style) || isAwning(type, style) ? all.filter((s) => !s.endsWith('half')) : all);
export const glassOptionsFor = (type, style, all = []) => (hasBottomSash(type, style) ? all : all.filter((g) => !g.endsWith('_bottom')));
export const grilleOptionsFor = (type, style, all = [], casementOnly = []) => (isCasement(type, style) ? [...all, ...casementOnly.filter((g) => !all.includes(g))] : all.filter((g) => !casementOnly.includes(g)));
// Tabela de preço usada (espelho de services/sales_pricing.price_type_for): estilo escolhido manda, exceto portas e formas.
export const priceTypeFor = (type, style, styleToType = {}) => {
  if (isDoorType(type) || isShapeType(type) || ALWAYS_FIXED.includes(type) || !style || style === 'keep') return type;
  const target = styleToType[style];
  if (!target) return type;
  return target === 'BAY' && (type === 'BAY' || type === 'BOW') ? type : target;
};

// Nova abertura desenhada pelo usuário (sem detecção) → item de windows[]
export const newWindowFromBox = (box, n, door = false) => ({ n, type: door ? 'NEW_DOOR' : 'NEW', position: '', box, is_new: true, replace: true, style: null, grille: null, color_id: null, glass_type: null, screen: null, exterior_trim: null, interior_trim: null, twin: false, units: 1 });

const MIN = 20;
const clamp = (v) => Math.max(0, Math.min(1000, v));
const applyDrag = (start, mode, dx, dy) => {
  let [x0, y0, x1, y1] = start;
  if (mode === 'move') {
    const w = x1 - x0, h = y1 - y0;
    x0 = Math.min(clamp(x0 + dx), 1000 - w); y0 = Math.min(clamp(y0 + dy), 1000 - h);
    x1 = x0 + w; y1 = y0 + h;
  } else {
    if (mode.includes('w')) x0 = clamp(Math.min(x0 + dx, x1 - MIN));
    if (mode.includes('e')) x1 = clamp(Math.max(x1 + dx, x0 + MIN));
    if (mode.includes('n')) y0 = clamp(Math.min(y0 + dy, y1 - MIN));
    if (mode.includes('s')) y1 = clamp(Math.max(y1 + dy, y0 + MIN));
  }
  return [Math.round(x0), Math.round(y0), Math.round(x1), Math.round(y1)];
};
export const boxStyle = (b) => ({ left: `${b[0] / 10}%`, top: `${b[1] / 10}%`, width: `${(b[2] - b[0]) / 10}%`, height: `${(b[3] - b[1]) / 10}%` });
const HANDLES = [['nw', '-top-2.5 -left-2.5 cursor-nwse-resize'], ['ne', '-top-2.5 -right-2.5 cursor-nesw-resize'], ['sw', '-bottom-2.5 -left-2.5 cursor-nesw-resize'], ['se', '-bottom-2.5 -right-2.5 cursor-nwse-resize']];

// Quadro editável sobre a foto: toque seleciona; selecionado → arrastar o corpo move e os cantos redimensionam. box 0-1000.
// Deve ser filho direto do stage (position: relative) com a mesma proporção da imagem.
export const EditableBox = ({ box, active, onPick, onChange, className = '', handleClass = 'border-teal-600', children, testId, style: extraStyle }) => {
  const ref = useRef(null);
  const drag = useRef(null);
  const moved = useRef(false);
  const [live, setLive] = useState(null);
  const b = live || box;
  const down = (e) => {
    if (!active || !onChange) return;
    e.stopPropagation();
    ref.current.setPointerCapture?.(e.pointerId);
    moved.current = false;
    drag.current = { mode: e.target.dataset.handle || 'move', sx: e.clientX, sy: e.clientY, start: [...box], live: null };
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const r = ref.current.offsetParent.getBoundingClientRect();
    if (Math.abs(e.clientX - d.sx) + Math.abs(e.clientY - d.sy) > 3) moved.current = true;
    d.live = applyDrag(d.start, d.mode, ((e.clientX - d.sx) / r.width) * 1000, ((e.clientY - d.sy) / r.height) * 1000);
    setLive(d.live);
  };
  const end = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.live && moved.current) onChange(d.live);
    setLive(null);
  };
  const click = (e) => { e.stopPropagation(); if (moved.current) { moved.current = false; return; } onPick?.(); };
  return (
    <div ref={ref} role="button" tabIndex={0} onClick={click} onPointerDown={down} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
      className={`absolute border-2 rounded-lg select-none ${active ? 'touch-none cursor-move ring-2 ring-white shadow-xl z-10' : 'cursor-pointer'} ${className}`} style={{ ...boxStyle(b), ...extraStyle }} data-testid={testId}>
      {children}
      {active && onChange && HANDLES.map(([h, cls]) => <span key={h} data-handle={h} className={`absolute h-5 w-5 rounded-full bg-white border-2 shadow ${handleClass} ${cls}`} data-testid={`${testId}-handle-${h}`} />)}
    </div>
  );
};
